import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { deviceGroupService, deviceService } from '../services/api';

// Create Group Modal
const CreateGroupModal = ({ onClose, onSuccess, onError }) => {
  const [groupName, setGroupName] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!groupName.trim()) {
      onError('Group name is required');
      return;
    }
    setLoading(true);
    try {
      const response = await deviceGroupService.create({ groupName: groupName.trim() });
      onSuccess(response.message || 'Device group created');
    } catch (err) {
      onError(err.response?.data?.error || 'Failed to create group');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>👥 Create Device Group</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label>Group Name *</label>
              <input
                type="text"
                value={groupName}
                onChange={e => setGroupName(e.target.value)}
                placeholder="e.g., Mumbai Fleet, Delhi Cabs"
                minLength={2}
                maxLength={100}
                required
                autoFocus
              />
              <span className="form-help">2-100 characters. Must be unique.</span>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading || !groupName.trim()}>
              {loading ? 'Creating...' : 'Create Group'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Group Modal
const EditGroupModal = ({ group, onClose, onSuccess, onError }) => {
  const [groupName, setGroupName] = useState(group?.groupName || '');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!groupName.trim()) {
      onError('Group name is required');
      return;
    }
    setLoading(true);
    try {
      const response = await deviceGroupService.update(group.id, { groupName: groupName.trim() });
      onSuccess(response.message || 'Device group updated');
    } catch (err) {
      onError(err.response?.data?.error || 'Failed to update group');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>✏️ Edit Device Group</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label>Group ID</label>
              <input type="text" value={group?.id || ''} disabled className="input-disabled" />
            </div>
            <div className="form-group">
              <label>Group Name *</label>
              <input
                type="text"
                value={groupName}
                onChange={e => setGroupName(e.target.value)}
                minLength={2}
                maxLength={100}
                required
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Add Devices to Group Modal
const AddDevicesModal = ({ group, onClose, onSuccess, onError }) => {
  const [devices, setDevices] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  useEffect(() => {
    const fetchDevices = async () => {
      setLoading(true);
      try {
        const response = await deviceService.getAll({ pageSize: 100, search: searchTerm });
        setDevices(response.data || []);
      } catch (err) {
        console.error('Error fetching devices:', err);
        onError?.('Failed to load devices');
      } finally {
        setLoading(false);
      }
    };
    fetchDevices();
  }, [searchTerm, onError]);

  const toggleDevice = (deviceId) => {
    setSelectedDevices(prev => 
      prev.includes(deviceId) 
        ? prev.filter(id => id !== deviceId)
        : [...prev, deviceId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedDevices.length === 0) {
      onError('Please select at least one device');
      return;
    }
    setSubmitting(true);
    try {
      const response = await deviceGroupService.addDevices(group.id, selectedDevices);
      onSuccess(response.message || `${selectedDevices.length} device(s) added to group`);
    } catch (err) {
      onError(err.response?.data?.error || 'Failed to add devices');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>➕ Add Devices to "{group?.groupName}"</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label>Search Devices</label>
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="🔍 Search by name, IMEI..."
              />
            </div>
            <div className="form-group">
              <label>Select Devices ({selectedDevices.length} selected)</label>
              {loading ? (
                <div className="loading-inline">Loading devices...</div>
              ) : devices.length === 0 ? (
                <div className="empty-state-small">No devices found</div>
              ) : (
                <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
                  {devices.map(device => (
                    <div 
                      key={device.id}
                      onClick={() => toggleDevice(device.id)}
                      style={{
                        padding: '0.75rem',
                        borderBottom: '1px solid #eee',
                        cursor: 'pointer',
                        background: selectedDevices.includes(device.id) ? '#e8f4fd' : 'white',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                      }}
                    >
                      <input 
                        type="checkbox" 
                        checked={selectedDevices.includes(device.id)}
                        onChange={() => {}}
                      />
                      <div>
                        <div style={{ fontWeight: 'bold' }}>{device.nodeName}</div>
                        <div style={{ fontSize: '0.85rem', color: '#666' }}>
                          {device.imei || device.serialNumber || device.widevineId || '—'}
                          {device.tenantName && <span> • {device.tenantName}</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting || selectedDevices.length === 0}>
              {submitting ? 'Adding...' : `Add ${selectedDevices.length} Device(s)`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// View Group Devices Modal
const ViewGroupDevicesModal = ({ group, onClose, onRemove }) => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removingId, setRemovingId] = useState(null);

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const fetchDevices = useCallback(async () => {
    setLoading(true);
    try {
      const response = await deviceGroupService.getDevices(group.id);
      setDevices(response.data || []);
    } catch (err) {
      console.error('Error fetching group devices:', err);
    } finally {
      setLoading(false);
    }
  }, [group.id]);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  const handleRemove = async (deviceId) => {
    setRemovingId(deviceId);
    try {
      await deviceGroupService.removeDevice(group.id, deviceId);
      fetchDevices();
      onRemove?.();
    } catch (err) {
      console.error('Error removing device:', err);
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>📱 Devices in "{group?.groupName}"</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {loading ? (
            <div className="loading-inline">Loading devices...</div>
          ) : devices.length === 0 ? (
            <div className="empty-state-small">No devices in this group</div>
          ) : (
            <div className="data-table">
              <table>
                <thead>
                  <tr>
                    <th>Device</th>
                    <th>Hardware ID</th>
                    <th>Tenant</th>
                    <th>City</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {devices.map(device => (
                    <tr key={device.id}>
                      <td>{device.nodeName}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                        {device.imei || device.serialNumber || device.widevineId || '—'}
                      </td>
                      <td>{device.tenantName || '—'}</td>
                      <td style={{ textTransform: 'capitalize' }}>{device.city || '—'}</td>
                      <td>
                        <button 
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleRemove(device.id)}
                          disabled={removingId === device.id}
                        >
                          {removingId === device.id ? '...' : '✕'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

// Main Device Groups Page
const DeviceGroups = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 25, totalCount: 0, totalPages: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  // V247: groups now have an owner. 'all' keeps this page's pre-V247 behaviour
  // (everything), but an admin can now isolate the groups they own from the
  // ones media owners manage themselves in media.pdooh.
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  // Modals state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editGroup, setEditGroup] = useState(null);
  const [addDevicesGroup, setAddDevicesGroup] = useState(null);
  const [viewDevicesGroup, setViewDevicesGroup] = useState(null);

  const fetchGroups = useCallback(async () => {
    try {
      setLoading(true);
      const response = await deviceGroupService.getAll({
        page: pagination.page,
        pageSize: pagination.pageSize,
        search: searchTerm,
        owner: ownerFilter,
      });
      setGroups(response.data || []);
      if (response.pagination) {
        setPagination(prev => ({ ...prev, ...response.pagination }));
      }
    } catch (err) {
      console.error('Error fetching groups:', err);
      setError('Failed to load device groups');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, searchTerm, ownerFilter]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  // Clear messages after 5 seconds
  useEffect(() => {
    if (message || error) {
      const timer = setTimeout(() => {
        setMessage(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message, error]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPagination({ ...pagination, page: 1 });
  };

  const handleDelete = async (group) => {
    // An operator-owned group is THEIR working artefact — they built it in
    // media.pdooh and it will vanish from their console without explanation.
    // Deleting it is still permitted (this is the super-admin console), but it
    // must not read like deleting an admin group.
    const prompt = group.owner === 'tenant'
      ? `"${group.groupName}" belongs to ${group.tenantName || 'an operator'}, who created and uses it in their own console.\n\n` +
        `Deleting it here removes it from THEIR view too, with no notice to them. Devices will NOT be deleted.\n\nDelete anyway?`
      : `Are you sure you want to delete "${group.groupName}"? Devices will NOT be deleted.`;

    if (!window.confirm(prompt)) {
      return;
    }
    setActionLoading(group.id);
    try {
      await deviceGroupService.delete(group.id);
      setMessage(`Group "${group.groupName}" deleted`);
      fetchGroups();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete group');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="users-page">
      <div className="page-header">
        <h1>👥 Device Groups</h1>
        <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
          + Create Group
        </button>
      </div>

      {message && (
        <div className="alert alert-success">✅ {message}</div>
      )}
      {error && (
        <div className="alert alert-error">❌ {error}</div>
      )}

      {/* Search + owner filter */}
      <div className="filters-bar">
        <div className="search-box">
          <input
            type="text"
            placeholder="🔍 Search groups by name..."
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>
        <select
          value={ownerFilter}
          onChange={(e) => {
            setOwnerFilter(e.target.value);
            setPagination(prev => ({ ...prev, page: 1 }));
          }}
          title="Who owns the group"
        >
          <option value="all">All owners</option>
          <option value="global">🌐 Global (admin)</option>
          <option value="tenant">🏢 Operator-owned</option>
        </select>
      </div>

      {/* Groups Table */}
      {loading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : (
        <>
          <div className="data-table">
            <table>
              <thead>
                <tr>
                  <th>Group Name</th>
                  <th>Owner</th>
                  <th>Devices</th>
                  <th>Created By</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {groups.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="empty-state">
                      No device groups found. <button onClick={() => setShowCreateModal(true)} className="link-button">Create your first group →</button>
                    </td>
                  </tr>
                ) : (
                  groups.map(group => (
                    <tr key={group.id}>
                      <td className="user-cell">
                        <div className="user-avatar">👥</div>
                        <div className="user-info">
                          <Link to={`/device-groups/${group.id}`} className="user-name" style={{ color: '#1976d2', textDecoration: 'none' }}>
                            {group.groupName}
                          </Link>
                        </div>
                      </td>
                      <td>
                        {group.owner === 'tenant' ? (
                          <span
                            title={`Owned by ${group.tenantName || 'an operator'} — they manage it in media.pdooh`}
                            style={{
                              background: '#ede7f6', color: '#4527a0', padding: '2px 8px',
                              borderRadius: 12, fontSize: '0.8rem', whiteSpace: 'nowrap',
                            }}
                          >
                            🏢 {group.tenantName || 'Operator'}
                          </span>
                        ) : (
                          <span
                            title="Global admin group — may span operators"
                            style={{
                              background: '#eceff1', color: '#37474f', padding: '2px 8px',
                              borderRadius: 12, fontSize: '0.8rem', whiteSpace: 'nowrap',
                            }}
                          >
                            🌐 Global
                          </span>
                        )}
                      </td>
                      <td>
                        <span
                          style={{
                            cursor: 'pointer',
                            color: '#1976d2',
                            textDecoration: 'underline'
                          }}
                          onClick={() => setViewDevicesGroup(group)}
                        >
                          {group.deviceCount} device{group.deviceCount !== 1 ? 's' : ''}
                        </span>
                      </td>
                      <td>{group.createdByEmail || '—'}</td>
                      <td>{formatDate(group.createdAt)}</td>
                      <td>
                        <div className="actions-dropdown">
                          <button className="btn btn-secondary btn-sm">
                            {actionLoading === group.id ? '...' : '⋮'}
                          </button>
                          <div className="dropdown-menu">
                            <button className="dropdown-item" onClick={() => setViewDevicesGroup(group)}>
                              📱 View Devices
                            </button>
                            <button className="dropdown-item" onClick={() => setAddDevicesGroup(group)}>
                              ➕ Add Devices
                            </button>
                            <button className="dropdown-item" onClick={() => setEditGroup(group)}>
                              ✏️ Rename Group
                            </button>
                            <button 
                              className="dropdown-item text-danger" 
                              onClick={() => handleDelete(group)}
                            >
                              🗑️ Delete Group
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalCount > 0 && pagination.totalPages > 1 && (
            <div className="pagination">
              <span>Showing {((pagination.page - 1) * pagination.pageSize) + 1}-{Math.min(pagination.page * pagination.pageSize, pagination.totalCount)} of {pagination.totalCount} groups</span>
              <div className="pagination-buttons">
                <button 
                  disabled={pagination.page === 1}
                  onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                >
                  ← Previous
                </button>
                <button 
                  disabled={pagination.page === pagination.totalPages}
                  onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modals */}
      {showCreateModal && (
        <CreateGroupModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={(msg) => { setMessage(msg); setShowCreateModal(false); fetchGroups(); }}
          onError={setError}
        />
      )}

      {editGroup && (
        <EditGroupModal
          group={editGroup}
          onClose={() => setEditGroup(null)}
          onSuccess={(msg) => { setMessage(msg); setEditGroup(null); fetchGroups(); }}
          onError={setError}
        />
      )}

      {addDevicesGroup && (
        <AddDevicesModal
          group={addDevicesGroup}
          onClose={() => setAddDevicesGroup(null)}
          onSuccess={(msg) => { setMessage(msg); setAddDevicesGroup(null); fetchGroups(); }}
          onError={setError}
        />
      )}

      {viewDevicesGroup && (
        <ViewGroupDevicesModal
          group={viewDevicesGroup}
          onClose={() => setViewDevicesGroup(null)}
          onRemove={fetchGroups}
        />
      )}
    </div>
  );
};

export default DeviceGroups;
